import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Bridge between the floating NotesPanel (rendered by ViewerWrapper) and the
 * ReportForm drawer. When the user clicks "Open report" in the notes panel,
 * we stash the text here and open the report drawer; ReportForm consumes the
 * text once features are resolved.
 */
export interface PendingNotes {
  text: string;
  /** Timestamp so repeat sends of the same text are recognized as a new push. */
  postedAt: number;
}

interface ViewerNotesContextValue {
  pending: PendingNotes | null;
  setPending: (text: string) => void;
  consume: () => PendingNotes | null;
}

const ViewerNotesContext = createContext<ViewerNotesContextValue | null>(null);

export const ViewerNotesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [pending, setPendingState] = useState<PendingNotes | null>(null);
  const pendingRef = useRef<PendingNotes | null>(null);
  pendingRef.current = pending;

  const setPending = useCallback((text: string) => {
    const next: PendingNotes = { text, postedAt: Date.now() };
    pendingRef.current = next;
    setPendingState(next);
  }, []);

  const consume = useCallback(() => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPendingState(null);
    return current;
  }, []);

  const value = useMemo(
    () => ({ pending, setPending, consume }),
    [pending, setPending, consume]
  );

  return (
    <ViewerNotesContext.Provider value={value}>
      {children}
    </ViewerNotesContext.Provider>
  );
};

/** Returns the context value or null if no provider is mounted (e.g. on /cases/:id/generate-report full-page). */
export function useViewerNotes(): ViewerNotesContextValue | null {
  return useContext(ViewerNotesContext);
}
